/**
 * Download quality tiers for Editing tool Download Options.
 * Credits sync with pricing catalog (Ai upscale = 3 for 4K path).
 * Adjust here / pricingModal — keep in sync.
 */

export const DOWNLOAD_QUALITY_TIER_IDS = ["1k", "2k", "4k"];

/** Longest-side thresholds (px). */
export const DOWNLOAD_QUALITY_MAX_SIDE = {
  "1k": 1024,
  "2k": 2048,
  "4k": 4096,
};

/**
 * Base catalog for download quality upgrades.
 * `credits` for upgrades; free when source already meets the tier.
 */
export const DOWNLOAD_QUALITY_TIERS = [
  {
    id: "1k",
    label: "1K",
    maxSide: DOWNLOAD_QUALITY_MAX_SIDE["1k"],
    credits: 0,
    softWatermark: true,
    description: "Free · soft watermark",
  },
  {
    id: "2k",
    label: "2K",
    maxSide: DOWNLOAD_QUALITY_MAX_SIDE["2k"],
    credits: 1,
    softWatermark: false,
    description: "High quality",
  },
  {
    id: "4k",
    label: "4K",
    maxSide: DOWNLOAD_QUALITY_MAX_SIDE["4k"],
    credits: 3,
    softWatermark: false,
    description: "Print / max quality",
  },
];

export function getDownloadQualityTier() {
  return DOWNLOAD_QUALITY_TIERS.map((t) => ({ ...t }));
}

export function getDownloadQualityTierById() {
  return Object.fromEntries(
    getDownloadQualityTier().map((t) => [t.id, t])
  );
}

/**
 * Build selectable quality rows from the source image's longest side.
 *
 * Rules:
 * - If source ≥ 4K → no quality row (caller hides).
 * - If source ≥ 2K → 1K disabled; current size free/clean; 4K paid upgrade.
 * - If source < 2K → 1K free+watermark; 2K/4K paid (upscale).
 */
export function buildDownloadQualityOptions(sourceLongestSide = 0) {
  const side = Math.max(0, Number(sourceLongestSide) || 0);
  const tiers = getDownloadQualityTier();

  if (side >= DOWNLOAD_QUALITY_MAX_SIDE["4k"]) {
    return [];
  }

  return tiers.map((tier) => {
    const is1k = tier.id === "1k";
    const is2k = tier.id === "2k";
    const is4k = tier.id === "4k";

    if (is1k && side >= DOWNLOAD_QUALITY_MAX_SIDE["2k"]) {
      return {
        ...tier,
        disabled: true,
        disabledReason: "Not available when your image is already 2K or larger",
        effectiveCredits: 0,
        isFree: false,
        isCurrent: false,
        hoverHint:
          "1K · longest side up to 1024px · Best for quick previews & sharing. Not available because your image is already 2K or larger.",
      };
    }

    const alreadyMeets =
      (is2k && side >= DOWNLOAD_QUALITY_MAX_SIDE["2k"]) ||
      (is4k && side >= DOWNLOAD_QUALITY_MAX_SIDE["4k"]) ||
      (is1k && side > 0 && side <= DOWNLOAD_QUALITY_MAX_SIDE["1k"]);

    const isCurrent =
      (is2k &&
        side >= DOWNLOAD_QUALITY_MAX_SIDE["2k"] &&
        side < DOWNLOAD_QUALITY_MAX_SIDE["4k"]) ||
      (is1k && side > 0 && side <= DOWNLOAD_QUALITY_MAX_SIDE["1k"]);

    const needsUpscale = side > 0 && side < tier.maxSide && !is1k;
    const isFree =
      alreadyMeets ||
      isCurrent ||
      (is1k && !alreadyMeets) ||
      tier.credits === 0;

    let effectiveCredits = 0;
    if (needsUpscale) {
      effectiveCredits = tier.credits;
    } else if (is1k) {
      effectiveCredits = 0;
    } else if (!alreadyMeets && !isCurrent && side < tier.maxSide) {
      effectiveCredits = tier.credits;
    }

    // ≥2K source downloading "2K"/current → free clean
    if (is2k && side >= DOWNLOAD_QUALITY_MAX_SIDE["2k"]) {
      effectiveCredits = 0;
    }

    const willUpscale = Boolean(needsUpscale && effectiveCredits > 0);
    const withWatermark = Boolean(is1k && effectiveCredits === 0);

    return {
      ...tier,
      disabled: false,
      disabledReason: "",
      effectiveCredits,
      isFree: effectiveCredits === 0,
      isCurrent: Boolean(isCurrent),
      needsUpscale: willUpscale,
      softWatermark: withWatermark,
      hoverHint: buildQualityHoverHint({
        id: tier.id,
        maxSide: tier.maxSide,
        effectiveCredits,
        softWatermark: withWatermark,
        needsUpscale: willUpscale,
        isCurrent: Boolean(isCurrent),
      }),
    };
  });
}

function buildQualityHoverHint({
  id,
  maxSide,
  effectiveCredits,
  softWatermark,
  needsUpscale,
  isCurrent,
}) {
  const sizeLine = `Longest side up to ${maxSide.toLocaleString()}px`;
  if (id === "1k") {
    return [
      `1K · ${sizeLine}`,
      "Good for web, chat & quick sharing",
      softWatermark ? "Includes a light LazyStudio watermark" : "Clean download",
      "Free",
    ].join(" · ");
  }
  if (id === "2k") {
    return [
      `2K · ${sizeLine}`,
      "Sharp on phones & laptops",
      isCurrent ? "Your current size" : needsUpscale ? "AI upscale included" : "Clean, no watermark",
      effectiveCredits > 0
        ? `${effectiveCredits} Credit${effectiveCredits === 1 ? "" : "s"}`
        : "Free",
    ].join(" · ");
  }
  return [
    `4K · ${sizeLine}`,
    "Best for print & large displays",
    needsUpscale ? "AI upscale included" : "Clean, no watermark",
    effectiveCredits > 0
      ? `${effectiveCredits} Credit${effectiveCredits === 1 ? "" : "s"}`
      : "Free",
  ].join(" · ");
}

export function defaultDownloadQualityId(options = []) {
  const enabled = options.filter((o) => !o.disabled);
  const free = enabled.find((o) => o.isFree);
  return (free || enabled[0])?.id || "1k";
}
